import { createFileRoute, Link } from '@tanstack/react-router';

export const Route = createFileRoute('/services/small-business')({
  component: SmallBusinessPage,
  head: () => ({
    meta: [
      { title: 'Small business & primary producer guide — Warren-Blackwood Electorate Guide' },
      {
        name: 'description',
        content:
          'Plain-English Warren-Blackwood guide for small business owners, farmers, tourism operators and tradies: SBDC, SBDC Regional, ATO essentials, Agrifood SW, tourism resources and land-use planning.',
      },
      { property: 'og:title', content: 'Small business guide — Warren-Blackwood' },
      {
        property: 'og:description',
        content:
          'SBDC, SBDC Regional, ATO essentials, Agrifood South West, tourism networks and a plain-English explainer on land use and planning approvals.',
      },
    ],
  }),
});

function SmallBusinessPage() {
  const h2: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.5rem',
    color: 'var(--rivergum)',
    marginTop: 'var(--sp-xl)',
    marginBottom: 'var(--sp-sm)',
    borderBottom: '1px solid var(--light-border)',
    paddingBottom: '0.3rem',
    letterSpacing: '0.02em',
  };
  const h3: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.1rem',
    color: 'var(--ink)',
    marginTop: 'var(--sp-md)',
    marginBottom: '0.4rem',
    letterSpacing: '0.02em',
  };
  const p: React.CSSProperties = {
    color: 'var(--ink)',
    lineHeight: 1.65,
    marginBottom: 'var(--sp-sm)',
  };
  const link: React.CSSProperties = { color: 'var(--green-mid)', fontWeight: 600 };
  const callout = (bg: string, border: string): React.CSSProperties => ({
    background: bg,
    border: `1px solid ${border}`,
    borderRadius: 8,
    padding: 'var(--sp-md)',
    marginBottom: 'var(--sp-md)',
  });

  return (
    <main
      id="main-content"
      style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: 'var(--sp-lg) var(--sp-md) var(--sp-xl)',
      }}
    >
      <p
        style={{
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          fontSize: '0.75rem',
          fontWeight: 700,
          color: 'var(--green-mid)',
          margin: 0,
          marginBottom: '0.35rem',
        }}
      >
        Services · Small business &amp; primary producers
      </p>
      <h1
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(2rem, 5vw, 2.75rem)',
          color: 'var(--green)',
          margin: 0,
          marginBottom: 'var(--sp-md)',
          lineHeight: 1.1,
        }}
      >
        Small business &amp; primary producer guide
      </h1>
      <p
        style={{
          color: 'var(--ink-mid)',
          fontStyle: 'italic',
          fontSize: '1.05rem',
          marginBottom: 'var(--sp-lg)',
          lineHeight: 1.5,
        }}
      >
        Warren-Blackwood runs on small operators — orchardists, vignerons,
        cattle farmers, cellar doors, cabin owners, cafes, sparkies and sole
        traders. This page is the plain-English starting point for advisory
        services, tax basics, sector networks and the land-use questions that
        trip up regional small business owners.
      </p>

      <div style={callout('#eef5fb', '#b9d4e8')}>
        <strong style={{ color: '#1b4f72' }}>Quick contacts</strong>
        <p style={{ ...p, margin: '0.4rem 0 0', fontSize: '0.95rem' }}>
          Small Business Development Corporation — <a href="tel:133140" style={link}>13 31 40</a> (Mon–Fri 8.30am–4.30pm)
          <br />
          ATO Small Business line — <a href="tel:132866" style={link}>13 28 66</a>
          <br />
          Rural Financial Counselling Service WA — <a href="tel:1800612004" style={link}>1800 612 004</a> (free, confidential)
          <br />
          NewAccess for Small Business Owners (Beyond Blue) — <a href="tel:1300945301" style={link}>1300 945 301</a>
        </p>
      </div>

      <h2 style={h2}>Start here: Small Business Development Corporation (SBDC)</h2>
      <p style={p}>
        The <strong>SBDC</strong> is the WA Government's free advisory service
        for small business owners — from people just thinking about an idea
        through to existing operators dealing with disputes, contracts or
        growth questions. Most services are <strong>free</strong> and
        confidential.
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>One-on-one advice</strong> by phone, video or in person — a
          business adviser will work through your specific situation.
        </li>
        <li>
          <strong>Workshops and webinars</strong> on tax, GST, marketing,
          digital skills, employment and finance — most are free or low-cost.
        </li>
        <li>
          <strong>Dispute resolution service</strong> for commercial tenancy
          disputes, supplier and contract issues, and franchising — handled
          before you have to go to court.
        </li>
        <li>
          <strong>Buying or selling a business</strong> — independent
          guidance on due diligence and structuring.
        </li>
      </ul>
      <p style={p}>
        Phone <a href="tel:133140" style={link}>13 31 40</a> or visit{' '}
        <a href="https://www.smallbusiness.wa.gov.au/" style={link}>smallbusiness.wa.gov.au →</a>
      </p>

      <h2 style={h2}>SBDC Regional</h2>
      <p style={p}>
        SBDC Regional — free small business advice in the South West.
      </p>
      <p style={p}>
        <a href="https://www.smallbusiness.wa.gov.au/regional" style={link}>
          SBDC Regional →
        </a>
      </p>

      <h2 style={h2}>ATO small business essentials</h2>
      <p style={p}>
        Most regional small business pain with the ATO is preventable. The
        non-negotiables:
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>ABN, GST &amp; PAYG</strong> — register through{' '}
          <a href="https://www.abr.gov.au/" style={link}>abr.gov.au</a>. GST is
          mandatory once turnover hits $75,000 ($150,000 for not-for-profits).
        </li>
        <li>
          <strong>BAS lodgement</strong> — quarterly for most small businesses.
          Use a registered BAS agent if you don't have a bookkeeper.
        </li>
        <li>
          <strong>Super for employees</strong> — Super Guarantee is 12% of
          ordinary time earnings (from 1 July 2025). Late payments aren't
          deductible and attract significant penalties — set up auto-pay
          through SuperStream.
        </li>
        <li>
          <strong>Single Touch Payroll (STP)</strong> — required for any
          employer, including those with just one employee. Most accounting
          software handles it.
        </li>
        <li>
          <strong>Instant asset write-off</strong> — the threshold changes
          year to year; check the current figure before buying equipment.
        </li>
        <li>
          <strong>Records</strong> — keep all business records for at least
          5 years.
        </li>
      </ul>
      <p style={p}>
        ATO Small Business line — <a href="tel:132866" style={link}>13 28 66</a>{' '}
        · <a href="https://www.ato.gov.au/businesses-and-organisations/small-business-newsroom" style={link}>ato.gov.au — small business →</a>
      </p>

      <h2 style={h2}>Primary producers &amp; agribusiness</h2>
      <p style={p}>
        Warren-Blackwood produces wine, beef, dairy, pome and stone fruit,
        avocados, truffles, marron and timber. Specialised support is
        available through the following:
      </p>
      <h3 style={h3}>Agrifood South West</h3>
      <p style={p}>
        <strong>Agrifood South West</strong> is the regional industry network
        for primary producers and value-add food businesses across the
        south-west — events, market access support, mentoring and connections
        to buyers.{' '}
        <a href="https://www.swdc.wa.gov.au/our-region/agrifood-and-fibre/" style={link}>
          South West Development Commission — Agrifood &amp; Fibre →
        </a>
      </p>
      <h3 style={h3}>DPIRD — Department of Primary Industries &amp; Regional Development</h3>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          DPIRD Manjimup office — local agronomic advice, biosecurity, grants.{' '}
          <a href="https://www.agric.wa.gov.au/" style={link}>agric.wa.gov.au →</a>
        </li>
        <li>
          <strong>Pest &amp; Disease Information Service</strong> —{' '}
          <a href="tel:1800084881" style={link}>1800 084 881</a> — free
          identification and management advice.
        </li>
      </ul>
      <h3 style={h3}>Rural Financial Counselling Service WA</h3>
      <p style={p}>
        Free, confidential and independent financial counselling for primary
        producers, fishers and small related businesses in financial
        difficulty. Counsellors travel to the farm.{' '}
        <a href="tel:1800612004" style={link}>1800 612 004</a> ·{' '}
        <a href="https://www.rfcswa.com.au/" style={link}>rfcswa.com.au →</a>
      </p>
      <h3 style={h3}>Other industry bodies</h3>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>WAFarmers</strong> — peak body for WA broadacre, livestock and dairy producers.{' '}
          <a href="https://www.wafarmers.org.au/" style={link}>wafarmers.org.au →</a>
        </li>
        <li>
          <strong>Wines of Western Australia</strong> — peak body for WA wine producers, including Margaret River and Geographe.{' '}
          <a href="https://winewa.asn.au/" style={link}>winewa.asn.au →</a>
        </li>
        <li>
          <strong>Pomewest</strong> &amp; <strong>Stonefruit WA</strong> — orchardist producer groups (Manjimup/Donnybrook focus).
        </li>
      </ul>

      <h2 style={h2}>Tourism operators</h2>
      <p style={p}>
        Tourism is one of the electorate's largest employers. The sector
        networks are worth joining the day you start trading.
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>Tourism Council WA</strong> — peak body for WA tourism
          operators; runs the Quality Tourism Accreditation Framework and the
          Top Tourism Town awards.{' '}
          <a href="https://www.tourismcouncilwa.com.au/" style={link}>tourismcouncilwa.com.au →</a>
        </li>
        <li>
          <strong>Australia's South West (ASW)</strong> — the regional tourism
          organisation covering the entire south-west, including Margaret
          River, Manjimup, Bridgetown and Nannup. Listings on{' '}
          <a href="https://www.australiassouthwest.com/" style={link}>australiassouthwest.com</a>{' '}
          drive most regional visitor traffic.
        </li>
        <li>
          <strong>Amazing South Coast</strong> — the regional tourism
          organisation for Denmark and the Great Southern.{' '}
          <a href="https://www.amazingsouthcoast.com/" style={link}>amazingsouthcoast.com →</a>
        </li>
        <li>
          <strong>Local destination groups</strong> — Margaret River Wine
          Association, Margaret River Busselton Tourism Association,
          Experience Manjimup, Bridgetown Tourism, Denmark Tourism Inc, and
          the Pemberton Visitor Centre all maintain local member directories.
        </li>
        <li>
          <strong>ATDW (Australian Tourism Data Warehouse)</strong> — the
          listings database that feeds Tourism WA, ASW and most visitor
          centre sites. One free listing here saves you keying details into a
          dozen platforms.
        </li>
      </ul>

      <h2 style={h2}>Land use &amp; planning approvals — plain English</h2>
      <p style={p}>
        This is the single biggest pain point we hear from small
        agribusiness, accommodation and on-farm food operators. The basics:
      </p>
      <h3 style={h3}>The zoning question comes first</h3>
      <p style={p}>
        Every property has a zone under the local <strong>Local Planning
        Scheme</strong>. Common rural zones in Warren-Blackwood include
        General Agriculture, Priority Agriculture, Rural Residential and
        Rural Smallholdings. The scheme tells you what uses are{' '}
        <strong>permitted (P)</strong>,{' '}
        <strong>discretionary (D / A)</strong>, or{' '}
        <strong>not permitted (X)</strong> in your zone.
      </p>
      <p style={p}>
        A "P" use means no planning approval is needed (you still need
        building approval where relevant). A "D" or "A" use means the council
        can approve it — usually with conditions — but won't always. An "X"
        use can't be approved.
      </p>
      <h3 style={h3}>Common rural small business scenarios</h3>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>Farm-stay / chalets / glamping</strong> — almost always
          requires planning approval. Density, effluent disposal and bushfire
          siting are the usual sticking points.
        </li>
        <li>
          <strong>Cellar door, farm-gate sales, on-farm cafe</strong> —
          usually a "Rural Pursuit" or "Tourism" use. Approval often
          conditional on opening hours, traffic management and signage.
        </li>
        <li>
          <strong>Value-add processing (cheese, smallgoods, preserves,
          distilling)</strong> — often classed as "Industry – Rural" or
          similar; may need separate Department of Health approval and a
          Food Business notification to the local shire's Environmental
          Health Officer.
        </li>
        <li>
          <strong>Events on a working farm</strong> — weddings, harvest
          dinners, weekend markets often need an Event Approval and may need
          a planning approval for change of use if recurring.
        </li>
        <li>
          <strong>Home-based business</strong> — most shires permit a
          modest home-based business without planning approval, subject to
          rules on staff numbers, visitor traffic and signage.
        </li>
      </ul>
      <h3 style={h3}>Bushfire (BAL) and the BPA</h3>
      <p style={p}>
        Most of Warren-Blackwood is a designated <strong>bushfire-prone
        area</strong>. Any new habitable building or change of use to
        habitable accommodation generally requires a{' '}
        <strong>Bushfire Attack Level (BAL) assessment</strong> from an
        accredited assessor, and a <strong>Bushfire Management
        Plan</strong> for higher-risk sites. Budget for this early — BAL-29
        or BAL-FZ siting can change the economics of a project.
      </p>
      <h3 style={h3}>How to navigate it without burning months</h3>
      <ol style={{ ...p, paddingLeft: '1.4rem' }}>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>Book a pre-lodgement meeting</strong> with your shire's
          planning team. It's free and saves drafting plans that won't pass.
          Each shire's planning contact is on its{' '}
          <Link to="/about" style={link}>shire page</Link>.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>Read your local planning scheme</strong> — search{' '}
          <a href="https://www.wa.gov.au/government/document-collections/local-planning-schemes" style={link}>
            wa.gov.au — Local Planning Schemes
          </a>{' '}
          for your shire's current scheme text.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>Get a planning consultant</strong> if your project is
          complex (cellar door + accommodation, large event, change of use).
          Their fee is dwarfed by the cost of a refused application.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>Talk to neighbours early</strong> — public submissions can
          delay or kill an application. Many objections are resolvable face
          to face.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>Don't start work without approval</strong> — retrospective
          approvals are slower, more expensive and not guaranteed.
        </li>
      </ol>
      <p style={p}>
        <a href="https://www.wa.gov.au/organisation/department-of-planning-lands-and-heritage" style={link}>
          Department of Planning, Lands &amp; Heritage →
        </a>
      </p>

      <h2 style={h2}>Mental health for business owners</h2>
      <p style={p}>
        Running a regional small business or farm is isolating, and the cash
        flow swings can be brutal. These services are free and built for
        small business owners specifically.
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>NewAccess for Small Business Owners</strong> (Beyond Blue) —{' '}
          <a href="tel:1300945301" style={link}>1300 945 301</a> — six free
          confidential coaching sessions, no GP referral needed.
        </li>
        <li>
          <strong>Ahead for Business</strong> — free digital mental health
          hub for small business owners.{' '}
          <a href="https://aheadforbusiness.org.au/" style={link}>aheadforbusiness.org.au →</a>
        </li>
        <li>
          <strong>Rural Financial Counselling Service WA</strong> —{' '}
          <a href="tel:1800612004" style={link}>1800 612 004</a> — counsellors
          handle the financial and emotional sides together.
        </li>
      </ul>

      <h2 style={h2}>When the MP's office can help</h2>
      <p style={p}>
        Most small business issues are best resolved through SBDC, your
        accountant, or your industry body. The electorate office can help
        with state-government interactions that have gone sideways:
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>Planning, building or biosecurity issues stuck with a state agency.</li>
        <li>State licensing delays (Liquor, Health, Transport).</li>
        <li>Drought, frost or fire recovery programs and disaster declarations.</li>
        <li>Concerns about state procurement and tender processes.</li>
        <li>Industry-wide issues you think the state should hear — these become advocacy.</li>
      </ul>
      <p style={p}>
        See the <Link to="/contact" style={link}>contact page</Link> for office hours.
      </p>

      <h2 style={h2}>Trusted sources</h2>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li><a href="https://www.smallbusiness.wa.gov.au/" style={link}>smallbusiness.wa.gov.au</a> — SBDC.</li>
        <li><a href="https://business.gov.au/" style={link}>business.gov.au</a> — federal small business portal.</li>
        <li><a href="https://www.ato.gov.au/businesses-and-organisations" style={link}>ato.gov.au — Businesses</a></li>
        <li><a href="https://www.agric.wa.gov.au/" style={link}>agric.wa.gov.au</a> — DPIRD.</li>
        <li><a href="https://www.swdc.wa.gov.au/" style={link}>swdc.wa.gov.au</a> — South West Development Commission.</li>
        <li><a href="https://www.tourismcouncilwa.com.au/" style={link}>tourismcouncilwa.com.au</a></li>
        <li><a href="https://www.fairwork.gov.au/" style={link}>fairwork.gov.au</a> — employment and award questions.</li>
      </ul>

      <p
        style={{
          marginTop: 'var(--sp-lg)',
          fontSize: '0.78rem',
          color: 'var(--ink-light)',
          borderTop: '1px solid var(--rule)',
          paddingTop: 'var(--sp-sm)',
          lineHeight: 1.5,
        }}
      >
        Community summary of publicly-available information from the SBDC,
        ATO, DPIRD and listed industry bodies. Thresholds and program details
        change — confirm current figures on the official sites above before
        acting. Nothing on this page is legal, tax or planning advice. Spot
        something out of date?{' '}
        <a
          href="mailto:bevan.eatts@mp.wa.gov.au?subject=Warren-Blackwood%20guide%20%E2%80%94%20small%20business%20page%20update"
          style={{ color: 'var(--green-mid)', fontWeight: 500 }}
        >
          Email the office
        </a>
        . Last reviewed: June 2026.
      </p>
    </main>
  );
}
